
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CampaignMiniBanner from '../../components/feature/CampaignMiniBanner';

export default function AboutCompany() {
  // State to control the navbar style on scroll
  const [scrolled, setScrolled] = useState(false);

  // Guard against SSR where `window` is not defined
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      try {
        setScrolled(window.scrollY > 50);
      } catch (err) {
        console.error('Error handling scroll event:', err);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const stats = [
    { number: '48+', label: 'プロジェクト実績' },
    { number: '12+', label: '製造パートナー拠点' },
    { number: '70%', label: '海外クライアント比率' },
    { number: '85%', label: 'リピート率' },
  ];

  const services = [
    { icon: 'ri-store-2-line', title: 'リテール', desc: '店舗什器・ディスプレイの設計・製造' },
    { icon: 'ri-calendar-event-line', title: 'イベント', desc: 'イベント空間・インスタレーションの企画・施工' },
    { icon: 'ri-building-line', title: 'ブランド体験', desc: 'ブランド体験空間のトータルプロデュース' },
    { icon: 'ri-cpu-line', title: 'IoT・デジタル', desc: 'インタラクティブ・IoT連携ソリューション' },
  ];

  return (
    <div className="relative">
      <Navbar scrolled={scrolled} />
      <div className="pt-16 sm:pt-20">
        <CampaignMiniBanner />
      </div>
      
      {/* Hero */}
      <section className="pt-8 sm:pt-12 pb-12 sm:pb-20 bg-black" aria-labelledby="company-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <Link
            to="/"
            className="inline-flex items-center text-white/60 hover:text-white mb-6 sm:mb-8 transition-colors cursor-pointer tracking-wide text-sm sm:text-base"
          >
            <i className="ri-arrow-left-line mr-2" aria-hidden="true"></i>
            トップに戻る
          </Link>
          <div className="max-w-4xl">
            <p className="text-[#8E7AB5] font-light mb-3 sm:mb-4 tracking-[0.1em] sm:tracking-[0.15em] text-sm sm:text-base">
              MeVERブランド運営会社
            </p>
            <h1
              id="company-title"
              className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-extralight text-white mb-6 sm:mb-8 tracking-[0.05em] sm:tracking-[0.1em]"
            >
              BITMOVES<br />JAPAN INC.
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl font-extralight text-white/80 mb-4 sm:mb-6 tracking-wide">
              Bridging Worlds. Building Trust. Driving Value.
            </p>
            <p className="text-sm sm:text-base md:text-lg font-extralight text-white/60 leading-relaxed max-w-2xl tracking-wide">
              ビットムーブスジャパンは、リテール・イベント・ブランド体験の領域で、什器からインタラクティブ・IoT連携インスタレーションまで、エンドツーエンドのソリューションを提供しています。
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-10 sm:py-12 md:py-16 bg-gray-900" aria-label="会社実績">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <dt className="sr-only">{stat.label}</dt>
                <dd className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extralight text-[#8E7AB5] mb-1 sm:mb-2 tracking-wide">
                  {stat.number}
                </dd>
                <dd className="text-xs sm:text-sm md:text-base text-white/60 font-extralight tracking-wide">
                  {stat.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-16 sm:py-20 md:py-24 bg-white" aria-labelledby="philosophy-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-12 md:gap-16 items-center">
            <div>
              <h2
                id="philosophy-heading"
                className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black mb-6 sm:mb-8 tracking-[0.05em] sm:tracking-[0.1em]"
              >
                What lies behind what we create.
              </h2>
              <div className="space-y-4 sm:space-y-6 text-sm sm:text-base md:text-lg text-black/70 font-extralight leading-relaxed tracking-wide">
                <p>
                  「Making」は単なる製造ではありません。誰かのアイデア、ビジョン、戦略を、ユーザーとの意味のあるつながりを生み出す形に変えることです。
                </p>
                <p>
                  私たちは「何を作るか」だけでなく、常に「なぜ作るか」を問います。プロジェクトがエンドユーザーにどのような価値を届けるのか、どのような行動やコンバージョンが期待されるのか。
                </p>
                <p>
                  これらの問いを深く探求し、目標に沿った最適化された物理的・デジタルアウトプットを提案・製造します。
                </p>
              </div>
            </div>
            <article className="bg-gray-100 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12">
              <h3 className="text-xl sm:text-2xl font-extralight text-black mb-4 sm:mb-6 tracking-[0.05em] sm:tracking-[0.1em]">
                PMFの追求
              </h3>
              <p className="text-sm sm:text-base text-black/70 font-extralight leading-relaxed mb-4 sm:mb-6 tracking-wide">
                BITMOVESのユニークさは、単なる製造代理店ではないことです。私たちは、すべての什器、空間、体験において常にProduct-Market Fit（PMF）を考慮する共創パートナーです。
              </p>
              <p className="text-sm sm:text-base text-black/70 font-extralight leading-relaxed tracking-wide">
                UXに焦点を当てた空間計画から、データ統合サイネージ、IoT連携什器まで、デザインと測定可能な成果を一致させるマーケティング主導の製造戦略を開発・実行します。
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 sm:py-20 md:py-24 bg-gray-50" aria-labelledby="services-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2
            id="services-heading"
            className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black mb-10 sm:mb-12 md:mb-16 text-center tracking-[0.1em] sm:tracking-[0.15em]"
          >
            事業領域
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8" role="list">
            {services.map((service, index) => (
              <article
                key={index}
                className="bg-white rounded-lg sm:rounded-xl p-5 sm:p-6 md:p-8 hover:shadow-xl transition-shadow"
                role="listitem"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-[#8E7AB5]/10 rounded-lg sm:rounded-xl mb-4 sm:mb-6">
                  <i
                    className={`${service.icon} text-xl sm:text-2xl text-[#8E7AB5]`}
                    aria-hidden="true"
                  ></i>
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-light text-black mb-1.5 sm:mb-2 tracking-wide">
                  {service.title}
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-black/60 font-extralight tracking-wide">
                  {service.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* MeVER Brand */}
      <section className="py-16 sm:py-20 md:py-24 bg-white" aria-labelledby="mever-brand-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <article className="bg-black rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 lg:p-16">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
              <div>
                <p className="text-[#8E7AB5] font-light mb-3 sm:mb-4 tracking-[0.1em] sm:tracking-[0.15em] text-sm sm:text-base">
                  自社ブランド
                </p>
                <h2
                  id="mever-brand-heading"
                  className="text-2xl sm:text-3xl md:text-4xl font-extralight text-white mb-4 sm:mb-6 tracking-[0.1em] sm:tracking-[0.15em]"
                >
                  MeVER
                </h2>
                <p className="text-sm sm:text-base text-white/70 font-extralight leading-relaxed mb-6 sm:mb-8 tracking-wide">
                  MeVERは、ビットムーブスジャパンが展開するクリエイター向けワークスタイルブランドです。長年の什器製造・空間設計の知見を活かし、クリエイターの「はたらく」と「あそぶ」をひとつにする製品を開発しています。
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-[#8E7AB5] text-white text-sm sm:text-base font-light tracking-[0.1em] hover:bg-[#7A6A9E] transition-colors whitespace-nowrap rounded-md cursor-pointer"
                >
                  MeVER製品を見る
                  <i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
                </Link>
              </div>
              <div className="flex justify-center order-first lg:order-last">
                <img
                  src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/9687a9ed07026a2b79edd442f7404cde.png"
                  alt="MeVER ロゴ"
                  className="h-12 sm:h-14 md:h-16 w-auto"
                  loading="lazy"
                  width="200"
                  height="64"
                />
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-16 sm:py-20 md:py-24 bg-gray-50" aria-labelledby="company-info-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2
            id="company-info-heading"
            className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black mb-10 sm:mb-12 md:mb-16 text-center tracking-[0.1em] sm:tracking-[0.15em]"
          >
            会社概要
          </h2>
          <div className="bg-white rounded-lg sm:rounded-xl overflow-hidden">
            <dl>
              {[
                { label: '会社名', value: 'ビットムーブスジャパン株式会社（BITMOVES JAPAN INC.）' },
                {
                  label: '所在地',
                  value: '〒100-6213 東京都千代田区丸の内1-11-1 パシフィックセンチュリープレイス丸の内 13F',
                },
                {
                  label: '事業内容',
                  value:
                    'リテール什器・イベント空間・ブランド体験の企画・設計・製造、IoT・インタラクティブインスタレーション、自社ブランド「MeVER」の企画・販売',
                },
                { label: '対応言語', value: '日本語・英語' },
                { label: 'Webサイト', value: 'https://bitmoves.jp/', isLink: true },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`flex flex-col sm:flex-row sm:items-start px-5 sm:px-8 py-4 sm:py-5 ${
                    index !== 4 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <dt className="text-xs sm:text-sm text-black/50 font-extralight sm:w-32 md:w-40 mb-1 sm:mb-0 tracking-wide flex-shrink-0">
                    {item.label}
                  </dt>
                  {item.isLink ? (
                    <dd>
                      <a
                        href={item.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm sm:text-base text-[#8E7AB5] hover:underline cursor-pointer font-extralight tracking-wide break-all"
                      >
                        {item.value}
                        <span className="sr-only">（外部サイト）</span>
                      </a>
                    </dd>
                  ) : (
                    <dd className="text-sm sm:text-base text-black font-extralight tracking-wide">
                      {item.value}
                    </dd>
                  )}
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 md:py-24 bg-black" aria-labelledby="contact-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2
            id="contact-heading"
            className="text-2xl sm:text-3xl md:text-4xl font-extralight text-white mb-4 sm:mb-6 tracking-[0.1em] sm:tracking-[0.15em]"
          >
            お問い合わせ
          </h2>
          <p className="text-sm sm:text-base text-white/60 font-extralight mb-8 sm:mb-10 tracking-wide">
            プロジェクトのご相談、MeVER製品に関するお問い合わせはこちらから
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <a
              href="https://bitmoves.jp/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-white text-black text-sm sm:text-base font-light tracking-[0.1em] hover:bg-white/90 transition-colors whitespace-nowrap rounded-md cursor-pointer"
              aria-label="BITMOVES公式サイトを新しいタブで開く"
            >
              BITMOVES公式サイト
              <i className="ri-external-link-line ml-2" aria-hidden="true"></i>
            </a>
            <a
              href="https://tihhyh.readdy.co"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-[#8E7AB5] text-white text-sm sm:text-base font-light tracking-[0.1em] hover:bg-[#7A6A9E] transition-colors whitespace-nowrap rounded-md cursor-pointer"
              aria-label="法人のお客様向けページを新しいタブで開く"
            >
              法人のお客様はこちら
              <i className="ri-external-link-line ml-2" aria-hidden="true"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
